import React from 'react';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import Warning from 'components/Warning';
import BookingStatus from 'components/BookingStatus';
import StepSection from 'components/StepSection';
import {default as Modal, withModal} from 'components/Modal';
import ErrorBlock from 'components/ErrorBlock';

import {
  BookingType,
  Buttons,
  InfoSection,
  PaymentSummary,
  TenantCard
} from 'containers/FrontDesk/components/shared';

import {CLEANING_STATUSES_DISALLOWED_FOR_CHECK_IN} from 'ducks/allocations';
import {PERMISSIONS, requiresMultiplePermissions} from 'ducks/auth';
import {openModal, closeModal, MODALS as BASE_MODALS} from 'ducks/modals';
import {
  MODALS,
  fetchPaymentSummary,
  fetchRenewBookingInfo,
  renewBooking
} from 'ducks/frontDesk';

import {formatDate} from 'utils/datetime';

import './styles.css';

class RenewBookingModal extends React.Component<any> {
  componentDidMount() {
    const {data: {locationRef, newBooking}} = this.props;
    const bookingId = newBooking;

    this.props.fetchRenewBookingInfo({locationRef, bookingId}, [
      ...this.props.storeDataExtraActions(data => ({
        renewalInfo: data
      }))
    ]);

    this.props.fetchPaymentSummary({locationRef, bookingId}, [
      ...this.props.storeDataExtraActions(data => ({
        paymentSummary: data
      }))
    ]);
  }

  renewBooking = () => {
    const {data: {locationRef}} = this.props;

    this.props.requiresMultiplePermissions(
      {
        permissions: [
          {permission: PERMISSIONS.CREATE_CHECK_IN, locationRef},
          {permission: PERMISSIONS.CREATE_CHECK_OUT, locationRef}
        ],
        disableClose: false
      },
      this.openConfirmationModal
    );
  };

  openConfirmationModal = () => {
    const {data: {locationRef, oldBooking, newBooking}} = this.props;

    const confirmAction = () => {
      const extraActions = [
        ...this.props.fetchCompletedSectionExtraActions(),
        ...this.props.fetchPendingSectionExtraActions(),
        () => closeModal(BASE_MODALS.CONFIRM),
        () => closeModal(MODALS.RENEW_BOOKING)
      ];

      this.props.renewBooking(
        {locationRef, oldBooking, newBooking},
        extraActions
      );
    };

    const question = <div>Are you sure you want to complete this renewal?</div>;

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Complete renewal',
      question,
      confirmAction,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Complete'
    });
  };

  render() {
    const {data: {renewalInfo, paymentSummary, hasRoomMove}} = this.props;

    let info: Array<any> = [];

    const cleaningStatus = get(renewalInfo, 'cleaningStatus', '');

    const skipCleaningStatusCheck = get(
      renewalInfo,
      'skipCleaningStatusCheck',
      ''
    );

    const cleaningStatusDisallowed =
      skipCleaningStatusCheck !== true &&
      hasRoomMove &&
      CLEANING_STATUSES_DISALLOWED_FOR_CHECK_IN.includes(cleaningStatus);

    if (!isNil(renewalInfo)) {
      const {
        roomNumber,
        moveIn,
        moveOut,
        duration,
        bookingType,
        bookingOrganisation
      } = renewalInfo;
      info = [
        {header: 'Room', text: roomNumber},
        {header: 'Move-In', text: formatDate(moveIn)},
        {header: 'Move-Out', text: formatDate(moveOut)},
        {header: 'Duration', text: duration},
        {header: 'Booking Type', text: bookingType},
        {header: 'Booking Organisation', text: bookingOrganisation || 'N/A'}
      ];
    }

    const primaryTenant = get(renewalInfo, 'primaryTenant');
    const secondaryTenant = get(renewalInfo, 'secondaryTenant');

    const modalType = secondaryTenant ? Modal.MEDIUM : Modal.SUB_MEDIUM;
    return (
      <React.Fragment>
        <Modal
          show={true}
          onClose={this.props.closeModal}
          type={modalType}
          className="RenewBookingModal">
          <Modal.Header>
            <BookingType bookingType={get(renewalInfo, 'bookingType')} />
            <div>
              <h3 className="RenewBookingModal__heading">
                {get(renewalInfo, 'bookingType')} {!hasRoomMove && 'Renew'}
                {hasRoomMove && 'Room Change & Renew'}
              </h3>
              {renewalInfo && (
                <BookingStatus status={renewalInfo.bookingStatus} />
              )}
            </div>
          </Modal.Header>
          <Modal.Body>
            {renewalInfo && (
              <div>
                <InfoSection info={info} />

                {cleaningStatusDisallowed && (
                  <ErrorBlock className="roomStatusError">
                    {`Allocated room is "${cleaningStatus.toLowerCase()}". Please change the room or update
                    the room housekeeping status.`}
                  </ErrorBlock>
                )}

                {!renewalInfo.agreementSigned && (
                  <Warning className="licenseAgreementWarning">
                    There is no completed agreement for this booking
                  </Warning>
                )}
                <StepSection
                  step="1"
                  header="Check that the member's personal details are up to date">
                  <TenantCard tenant={primaryTenant} />
                  {secondaryTenant && <TenantCard tenant={secondaryTenant} />}
                </StepSection>

                <StepSection step="2" header="Payment summary">
                  <div className="RenewBookingModal__paymentSummary">
                    <PaymentSummary paymentSummary={paymentSummary} />
                    {!primaryTenant.activeGocardlessCustomer && (
                      <Warning>
                        The member has not yet signed up for GoCardless
                      </Warning>
                    )}
                  </div>
                </StepSection>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Buttons
              closeModal={this.props.closeModal}
              onSubmit={this.renewBooking}
              active={!cleaningStatusDisallowed}
              submitting={false}
              label="Renew"
            />
          </Modal.Footer>
        </Modal>
      </React.Fragment>
    );
  }
}

const mapStateToModalData = _state => {
  return {};
};

const mapDispatchToProps = {
  openModal,
  fetchPaymentSummary,
  fetchRenewBookingInfo,
  requiresMultiplePermissions,
  renewBooking
};

export default withModal(
  MODALS.RENEW_BOOKING,
  mapStateToModalData,
  mapDispatchToProps
)(RenewBookingModal);
